import React, {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
  Switch,
  FormControlLabel,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  OpenInNew as OpenIcon,
  Restaurant as RestaurantIcon,
  BusinessCenter as SponsorIcon,
  VideoLibrary as VideoIcon,
  Leaderboard as RankingIcon,
  ViewModule as LayoutIcon,
  Timer as TimerIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import {
  liveService,
} from '../../services/live';
import {
  timeTrialService,
} from '../../services/timeTrials';

const LiveManagement = () => {
  // State for configuration
  const [config, setConfig] = useState({
    event_id: '',
    event_name: '',
    video_stream_url: '',
    selected_ranking_type: 'tournament',
    selected_time_trials: [],
    layout_config: {
      event_name: { x: 0, y: 0, w: 12, h: 1, enabled: true },
      video_feed: { x: 0, y: 1, w: 8, h: 6, enabled: true },
      restaurants: { x: 8, y: 1, w: 4, h: 3, enabled: true },
      tournaments: { x: 8, y: 4, w: 4, h: 3, enabled: true },
      chat: { x: 0, y: 7, w: 6, h: 4, enabled: true },
      rankings: { x: 6, y: 7, w: 6, h: 4, enabled: true },
      sponsors: { x: 0, y: 11, w: 12, h: 2, enabled: true }
    }
  });

  // State for time trials
  const [timeTrials, setTimeTrials] = useState([]);

  // State for restaurants
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantDialog, setRestaurantDialog] = useState(false);
  const [currentRestaurant, setCurrentRestaurant] = useState(null);
  const [restaurantForm, setRestaurantForm] = useState({ name: '', description: '', status: 'open' });

  // State for sponsors
  const [sponsors, setSponsors] = useState([]);
  const [sponsorDialog, setSponsorDialog] = useState(false);
  const [currentSponsor, setCurrentSponsor] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({ name: '', logo_url: '', website: '', order: 0 });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState('');

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [configResult, restaurantsResult, sponsorsResult, timeTrialsResult] = await Promise.all([
        liveService.getLiveConfig(),
        liveService.getRestaurants(),
        liveService.getSponsors(),
        timeTrialService.getTimeTrials()
      ]);

      if (configResult.success && configResult.data) {
        setConfig(configResult.data);
      }

      if (restaurantsResult.success) {
        setRestaurants(restaurantsResult.data);
      }

      if (sponsorsResult.success) {
        setSponsors(sponsorsResult.data);
      }

      if (timeTrialsResult.success) {
        setTimeTrials(timeTrialsResult.data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load live configuration data');
    } finally {
      setLoading(false);
    }
  };

  // ==================== Configuration Handlers ====================

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleTimeTrialSelection = (event, newValue) => {
    // newValue will be an array of time trial objects
    const selectedIds = newValue.map(trial => trial.id || trial._id);
    setConfig(prev => ({ ...prev, selected_time_trials: selectedIds }));
  };

  const handleLayoutToggle = (section) => {
    setConfig(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        [section]: {
          ...prev.layout_config[section],
          enabled: !prev.layout_config[section].enabled
        }
      }
    }));
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    const result = await liveService.updateLiveConfig(config);

    if (result.success) {
      setSnackbar({ open: true, message: 'Configuration saved successfully!', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.error || 'Failed to save configuration', severity: 'error' });
    }
    setSaving(false);
  };

  const handleOpenLiveDisplay = () => {
    window.open('/live', '_blank', 'noopener,noreferrer');
  };

  // ==================== Restaurant Handlers ====================

  const handleAddRestaurant = () => {
    setCurrentRestaurant(null);
    setRestaurantForm({ name: '', description: '', status: 'open' });
    setRestaurantDialog(true);
  };

  const handleEditRestaurant = (restaurant) => {
    setCurrentRestaurant(restaurant);
    setRestaurantForm({ 
      name: restaurant.name, 
      description: restaurant.description || '', 
      status: restaurant.status || 'open' 
    });
    setRestaurantDialog(true);
  };

  const handleSaveRestaurant = async () => {
    if (!restaurantForm.name.trim()) {
      setSnackbar({ open: true, message: 'Restaurant name is required', severity: 'error' });
      return;
    }

    setSaving(true);
    let result;

    if (currentRestaurant) {
      result = await liveService.updateRestaurant(currentRestaurant.id, restaurantForm);
    } else {
      result = await liveService.createRestaurant(restaurantForm);
    }

    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      setRestaurantDialog(false);
      await loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
    setSaving(false);
  };

  const handleDeleteRestaurant = async (restaurantId) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) return;

    const result = await liveService.deleteRestaurant(restaurantId);
    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      await loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  // ==================== Sponsor Handlers ====================

  const handleAddSponsor = () => {
    setCurrentSponsor(null);
    setSponsorForm({ name: '', logo_url: '', website: '', order: sponsors.length });
    setSponsorDialog(true);
  };

  const handleEditSponsor = (sponsor) => {
    setCurrentSponsor(sponsor);
    setSponsorForm({ 
      name: sponsor.name, 
      logo_url: sponsor.logo_url, 
      website: sponsor.website || '', 
      order: sponsor.order 
    });
    setSponsorDialog(true);
  };

  const handleSaveSponsor = async () => {
    if (!sponsorForm.name.trim() || !sponsorForm.logo_url.trim()) {
      setSnackbar({ open: true, message: 'Sponsor name and logo URL are required', severity: 'error' });
      return;
    }

    setSaving(true);
    let result;

    if (currentSponsor) {
      result = await liveService.updateSponsor(currentSponsor.id, sponsorForm);
    } else {
      result = await liveService.createSponsor(sponsorForm);
    }

    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      setSponsorDialog(false);
      await loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
    setSaving(false);
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;

    const result = await liveService.deleteSponsor(sponsorId);
    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      await loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Live Display Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveConfig}
            disabled={saving}
          >
            Save Configuration
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<OpenIcon />}
            onClick={handleOpenLiveDisplay}
          >
            Open Live Display
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Video Configuration */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VideoIcon color="primary" />
              <Typography variant="h6">Video Configuration</Typography>
            </Box>
            <TextField
              fullWidth
              label="Event Name"
              value={config.event_name}
              onChange={(e) => handleConfigChange('event_name', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Video Stream URL (YouTube/Twitch Embed)"
              value={config.video_stream_url}
              onChange={(e) => handleConfigChange('video_stream_url', e.target.value)}
              placeholder="https://www.youtube.com/embed/..."
              helperText="Enter the embed URL for your video stream"
            />
          </Paper>
        </Grid>

        {/* Ranking Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RankingIcon color="primary" />
              <Typography variant="h6">Ranking Display</Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Ranking Type</InputLabel>
              <Select
                value={config.selected_ranking_type}
                onChange={(e) => handleConfigChange('selected_ranking_type', e.target.value)}
                label="Ranking Type"
              >
                <MenuItem value="tournament">Tournament Rankings</MenuItem>
                <MenuItem value="event">Event Rankings</MenuItem>
                <MenuItem value="global">Global Rankings</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        {/* Time Trial Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TimerIcon color="primary" />
              <Typography variant="h6">Time Trial Rankings</Typography>
            </Box>
            <Autocomplete
              multiple
              options={timeTrials}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name || 'Unnamed Trial'}
              value={timeTrials.filter(trial => 
                (config.selected_time_trials || []).includes(trial.id || trial._id)
              )}
              onChange={handleTimeTrialSelection}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.game_name || 'Unknown Game'} • {option.status}
                    </Typography>
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select time trials to display on live screen"
                  helperText="Choose which time trial rankings should be shown on the live display"
                />
              )}
            />
          </Paper>
        </Grid>

        {/* Layout Sections Toggle */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LayoutIcon color="primary" />
              <Typography variant="h6">Display Sections</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(config.layout_config).map(([key, section]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={section.enabled}
                      onChange={() => handleLayoutToggle(key)}
                    />
                  }
                  label={key.replace('_', ' ').toUpperCase()}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Restaurants Management */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RestaurantIcon color="primary" />
                <Typography variant="h6">Restaurants</Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRestaurant}
              >
                Add
              </Button>
            </Box>
            <List>
              {restaurants.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No restaurants added yet
                </Typography>
              ) : (
                restaurants.map((restaurant) => (
                  <ListItem key={restaurant.id} divider>
                    <ListItemText
                      primary={restaurant.name}
                      secondary={restaurant.description || restaurant.status}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleEditRestaurant(restaurant)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteRestaurant(restaurant.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        {/* Sponsors Management */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SponsorIcon color="primary" />
                <Typography variant="h6">Sponsors</Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSponsor}
              >
                Add
              </Button>
            </Box>
            <Grid container spacing={2}>
              {sponsors.length === 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No sponsors added yet
                  </Typography>
                </Grid>
              ) : (
                sponsors.map((sponsor) => (
                  <Grid item xs={6} sm={4} key={sponsor.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        {sponsor.logo_url && (
                          <img
                            src={sponsor.logo_url}
                            alt={sponsor.name}
                            style={{ maxWidth: '100%', maxHeight: 60, objectFit: 'contain', marginBottom: 8 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <Typography variant="body2" noWrap>{sponsor.name}</Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'center', p: 1 }}>
                        <IconButton size="small" onClick={() => handleEditSponsor(sponsor)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteSponsor(sponsor.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Restaurant Dialog */}
      <Dialog open={restaurantDialog} onClose={() => setRestaurantDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentRestaurant ? 'Edit Restaurant' : 'Add Restaurant'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Restaurant Name"
            value={restaurantForm.name}
            onChange={(e) => setRestaurantForm({ ...restaurantForm, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={restaurantForm.description}
            onChange={(e) => setRestaurantForm({ ...restaurantForm, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={restaurantForm.status}
              onChange={(e) => setRestaurantForm({ ...restaurantForm, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="busy">Busy</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestaurantDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveRestaurant} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sponsor Dialog */}
      <Dialog open={sponsorDialog} onClose={() => setSponsorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentSponsor ? 'Edit Sponsor' : 'Add Sponsor'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Sponsor Name"
            value={sponsorForm.name}
            onChange={(e) => setSponsorForm({ ...sponsorForm, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Logo URL"
            value={sponsorForm.logo_url}
            onChange={(e) => setSponsorForm({ ...sponsorForm, logo_url: e.target.value })}
            placeholder="https://example.com/logo.png"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Website (optional)"
            value={sponsorForm.website}
            onChange={(e) => setSponsorForm({ ...sponsorForm, website: e.target.value })}
            placeholder="https://example.com"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Display Order"
            type="number"
            value={sponsorForm.order}
            onChange={(e) => setSponsorForm({ ...sponsorForm, order: parseInt(e.target.value) || 0 })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSponsorDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSponsor} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LiveManagement;

